import { ApiResponse, FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable } from 'mobx';
import { DocReviewInternalService } from '../../../service/docReviewInternalService';
import {
  ADDRESS_PARTS,
  AddressValue,
  DRIVER_LICENSE_ID_TYPE,
  IdentityValues,
  US_CITIZEN,
  buildInitialValues,
  buildSaveValues,
  toAmount,
} from './identityReviewValues';

const REQUIRED = 'This field is required';

const form = {
  fields: {
    dateOfBirth: {
      value: null,
      error: null,
      rule: 'required',
      message: 'Please enter a valid Date of Birth',
    },
    idType: { value: null, error: null, rule: '' },
    issuingState: { value: null, error: null, rule: '' },
    idNumber: { value: null, error: null, rule: '' },
    citizenshipStatus: {
      value: '',
      error: null,
      rule: 'required',
      message: 'Please select a Citizenship Status',
    },
    expiryDate: { value: null, error: null, rule: '' },
    // Address line plus its parts, kept flat so each part gets its own rule and error.
    address: { value: '', error: null, rule: '' },
    streetNumber: { value: '', error: null, rule: 'required', message: REQUIRED },
    streetName: { value: '', error: null, rule: 'required', message: REQUIRED },
    aptNumber: { value: '', error: null, rule: '' },
    city: { value: '', error: null, rule: 'required', message: REQUIRED },
    state: { value: '', error: null, rule: 'required', message: REQUIRED },
    zipCode: { value: '', error: null, rule: 'required', message: REQUIRED },
    assets: { value: null, error: null, rule: '' },
    liabilities: { value: null, error: null, rule: '' },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class IdentityFormStore extends FormStore {
  globalStore: GlobalStore;
  private initial: IdentityValues = {};
  private borrowerId: number;
  private service: DocReviewInternalService;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.service = new DocReviewInternalService();
    makeObservable(this, {
      values: computed,
      netWorth: computed,
      onEditFieldChange: action,
      changeIdType: flow,
    });
  }

  // The form's values with the address parts folded back into one AddressValue.
  get values(): IdentityValues {
    const values = this.getFlattenedValues('value') as IdentityValues;
    const address: AddressValue = { address: values.address };
    ADDRESS_PARTS.forEach(({ key }) => {
      address[key] = values[key];
      delete values[key];
    });
    return { ...values, address };
  }

  get netWorth(): number {
    const { assets, liabilities } = this.form.fields;
    return (toAmount(assets.value) ?? 0) - (toAmount(liabilities.value) ?? 0);
  }

  load(data: Record<string, any>, borrowerId: number) {
    this.borrowerId = borrowerId;
    this.initial = buildInitialValues(data ?? {});
    this.reset();
    this.loadForm({
      ...this.initial,
      ...this.initial.address,
    });
  }

  // Entry point for the edit modal. Not an onFieldChange override, because
  // runFormValidation calls onFieldChange for every field with its raw value.
  onEditFieldChange(field: string, value: any) {
    if (field === 'idType') {
      this.changeIdType(value);
      return;
    }
    if (field === 'address') {
      const { address, ...parts } = (value ?? {}) as AddressValue;
      this.onFieldChange('address', address ?? '');
      Object.keys(parts).forEach(key => this.onFieldChange(key, parts[key] ?? ''));
      return;
    }
    this.onFieldChange(field, value);
  }

  *changeIdType(idType: string) {
    this.onFieldChange('idType', idType);
    if (idType === DRIVER_LICENSE_ID_TYPE) {
      this.onFieldChange('citizenshipStatus', US_CITIZEN);
    }
    try {
      const response: ApiResponse = yield this.service.getBorrowerIdentityData(this.borrowerId, idType);
      if (this.form.fields.idType.value !== idType) return;
      const identity = response?.data?.data ?? {};
      this.onFieldChange('issuingState', identity.state ?? null);
      this.onFieldChange('idNumber', identity.idNumber ?? null);
      this.onFieldChange('expiryDate', identity.expirationDate ?? null);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error getting the borrower identity data for this ID type',
      });
    }
  }

  // get custom payload for the identity form.
  getSaveValues = (): Record<string, any> => buildSaveValues(this.values, this.initial);
}
